import { CheckIcon, ChevronRightIcon, MegaphoneIcon } from "lucide-react";
import { useState, type ComponentType } from "react";

import { PersonAvatar } from "~/components/identity/PersonAvatar";
import { WorkspaceAvatar } from "~/components/identity/WorkspaceAvatar";
import { cn } from "~/lib/utils";
import type { DataViewController } from "~/views/viewController";
import { PLANNING_DISPLAY_SECTION_LABEL, planningDisplayRowClass } from "./planningDisplayChrome";
import { SavedViewsWidget } from "./SavedViewsMenu";

export type ViewsFilterItemVisual =
  | { readonly kind: "campaign"; readonly label: string }
  | { readonly kind: "dot"; readonly className: string }
  | { readonly kind: "owner"; readonly avatarUrl: string | null; readonly label: string }
  | {
      readonly kind: "workspace";
      readonly iconUrl: string | null;
      readonly logoUrl: string | null;
      readonly label: string;
    };

export interface ViewsFilterItem {
  readonly value: string;
  readonly label: string;
  readonly count?: number;
  readonly visual?: ViewsFilterItemVisual;
}

export interface ViewsFilterCategory {
  readonly id: string;
  readonly label: string;
  readonly icon: ComponentType<{ className?: string }>;
  readonly value: string;
  readonly items: ReadonlyArray<ViewsFilterItem>;
  readonly onChange: (value: string) => void;
}

export interface ViewsAndFiltersPanelProps {
  readonly controller: DataViewController;
  readonly categories: ReadonlyArray<ViewsFilterCategory>;
  readonly initialCategoryId?: string;
  readonly className?: string;
  readonly showSavedViews?: boolean;
}

function selectedFilterItem(
  items: ReadonlyArray<ViewsFilterItem>,
  value: string,
): ViewsFilterItem | null {
  if (!value) return null;
  return items.find((item) => item.value === value) ?? { value, label: value };
}

function FilterItemVisualMark({ visual }: { readonly visual: ViewsFilterItemVisual }) {
  if (visual.kind === "dot") {
    return <span className={cn("size-1.5 shrink-0 rounded-full", visual.className)} aria-hidden />;
  }

  if (visual.kind === "workspace") {
    return (
      <WorkspaceAvatar
        name={visual.label}
        iconUrl={visual.iconUrl}
        logoUrl={visual.logoUrl}
        className="inline-flex size-4 shrink-0 items-center justify-center rounded-sm border border-border/60 bg-background text-[9px] font-semibold text-muted-foreground"
        fallbackClassName="bg-muted/50"
      />
    );
  }

  if (visual.kind === "owner") {
    return (
      <PersonAvatar
        name={visual.label}
        avatarUrl={visual.avatarUrl}
        className="inline-flex size-4 shrink-0 items-center justify-center rounded-full border border-border/60 bg-muted/50 text-[8px] font-semibold text-muted-foreground"
      />
    );
  }

  return (
    <span
      className="inline-flex size-4 shrink-0 items-center justify-center rounded-sm border border-border/60 bg-muted/50 text-muted-foreground"
      aria-hidden
    >
      <MegaphoneIcon className="size-2.5" />
    </span>
  );
}

function FilterOptionContent({ item }: { readonly item: ViewsFilterItem }) {
  return (
    <span className="flex min-w-0 items-center gap-2">
      {item.visual ? <FilterItemVisualMark visual={item.visual} /> : null}
      <span className="min-w-0 flex-1 truncate">{item.label}</span>
      {item.count !== undefined ? (
        <span className="shrink-0 font-mono text-[11px] tabular-nums text-muted-foreground/80">
          {item.count}
        </span>
      ) : null}
    </span>
  );
}

function FilterCategoryButton({
  category,
  selected,
  isActive,
  onSelect,
}: {
  readonly category: ViewsFilterCategory;
  readonly selected: ViewsFilterItem | null;
  readonly isActive: boolean;
  readonly onSelect: () => void;
}) {
  const Icon = category.icon;
  const hasValue = selected !== null;

  return (
    <button
      type="button"
      onClick={onSelect}
      aria-pressed={isActive}
      className={planningDisplayRowClass({
        selected: isActive,
        className: "pr-1.5",
      })}
    >
      <Icon
        className={cn(
          "size-3.5 shrink-0",
          hasValue || isActive ? "text-foreground" : "text-muted-foreground",
        )}
      />
      <span className="min-w-0 flex-1 truncate">{category.label}</span>
      {selected ? (
        <span className="flex min-w-0 max-w-[96px] items-center gap-1.5 text-[11px] font-medium text-foreground/90">
          {selected.visual ? <FilterItemVisualMark visual={selected.visual} /> : null}
          <span className="min-w-0 truncate">{selected.label}</span>
        </span>
      ) : null}
      <ChevronRightIcon className="size-3 shrink-0 text-muted-foreground/55" />
    </button>
  );
}

function FilterOptionList({
  label,
  value,
  items,
  onChange,
}: {
  readonly label: string;
  readonly value: string;
  readonly items: ReadonlyArray<ViewsFilterItem>;
  readonly onChange: (value: string) => void;
}) {
  return (
    <section
      data-views-filters-options
      className="flex min-h-0 flex-1 flex-col"
      aria-label={`${label} options`}
    >
      <div className="min-h-0 flex-1 overflow-y-auto px-2 py-2.5">
        <p className={PLANNING_DISPLAY_SECTION_LABEL}>{label}</p>
        <div className="flex flex-col gap-0.5">
          {items.map((item) => {
            const isSelected = item.value === value;
            return (
              <button
                key={item.value}
                type="button"
                onClick={() => onChange(item.value)}
                className={planningDisplayRowClass({ selected: isSelected })}
                aria-pressed={isSelected}
              >
                <span className="flex size-3.5 shrink-0 items-center justify-center text-foreground">
                  {isSelected ? <CheckIcon className="size-3" aria-hidden /> : null}
                </span>
                <span className="min-w-0 flex-1">
                  <FilterOptionContent item={item} />
                </span>
              </button>
            );
          })}
        </div>
      </div>
    </section>
  );
}

/**
 * Projects-page dual-pane chrome: saved views + filter categories on the left,
 * the active category's options on the right.
 */
export function ViewsAndFiltersPanel(props: ViewsAndFiltersPanelProps) {
  const showSavedViews = props.showSavedViews !== false;
  const fallbackCategoryId = props.categories[0]?.id ?? "";
  const [activeCategoryId, setActiveCategoryId] = useState(
    props.initialCategoryId ?? fallbackCategoryId,
  );
  const selectedCategory =
    props.categories.find((category) => category.id === activeCategoryId) ?? props.categories[0];

  if (!selectedCategory) {
    return (
      <div className={cn("flex min-h-0 flex-col", props.className)} data-views-filters-panel>
        {showSavedViews ? (
          <SavedViewsWidget controller={props.controller} className="min-h-0 flex-1 px-2 py-2.5" />
        ) : null}
      </div>
    );
  }

  return (
    <div
      data-views-filters-panel
      className={cn("flex max-h-[390px] min-h-[286px] flex-col overflow-hidden", props.className)}
    >
      <div className="flex min-h-0 flex-1 overflow-hidden">
        <div
          data-views-filters-nav
          className="flex w-[44%] min-w-[128px] shrink-0 flex-col overflow-hidden border-r border-border md:w-[238px]"
        >
          {showSavedViews ? (
            <SavedViewsWidget
              controller={props.controller}
              className="min-h-0 flex-1 px-2 py-2.5"
            />
          ) : null}
          <div
            className={cn(
              "px-2 py-2.5",
              showSavedViews ? "shrink-0 border-t border-border" : "min-h-0 flex-1 overflow-y-auto",
            )}
          >
            <p className={PLANNING_DISPLAY_SECTION_LABEL}>Filter</p>
            <div className="flex flex-col gap-0.5">
              {props.categories.map((category) => {
                const selected = selectedFilterItem(category.items, category.value);
                return (
                  <FilterCategoryButton
                    key={category.id}
                    category={category}
                    selected={category.value ? selected : null}
                    isActive={category.id === selectedCategory.id}
                    onSelect={() => setActiveCategoryId(category.id)}
                  />
                );
              })}
            </div>
          </div>
        </div>
        <FilterOptionList
          label={selectedCategory.label}
          value={selectedCategory.value}
          items={selectedCategory.items}
          onChange={selectedCategory.onChange}
        />
      </div>
    </div>
  );
}
